import * as React from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { useEditingTableCellName } from 'af-root/hooks/useEditingTableCellName';
import { createValidFromGlyph } from 'af-root/scenes/Company/Jobs/Preview/Materials/JobMaterialsTable/JobMaterialsTableForm/createValidFromGlyph';

import { DisplayCell } from 'af-components/RHFTableCellComponents/Input/DisplayCell';
import InputCellRenderer from 'af-components/RHFTableCellRenderers/InputCellRenderer';

import type { MaterialsFormInput, MaterialsFormOutput } from '../formModel';

interface OwnProps {
	containerClassName: string;
	defaultValue: number;
	fieldsLength: number;
	readonly: boolean;
	displayOnly: boolean;
	rhfId: string;
	name: `materials.${number}.futureRate.baseCost` | `materials.${number}.currentRate.baseCost`;
	/** DB_DATE_ONLY */
	validFrom?: Nullable<string>;
	control: UseFormReturn<MaterialsFormInput, unknown, MaterialsFormOutput>['control'];
	readonlyFieldClassName?: string;
	getValues: UseFormReturn<MaterialsFormInput, unknown, MaterialsFormOutput>['getValues'];
	unregister: UseFormReturn<MaterialsFormInput, unknown, MaterialsFormOutput>['unregister'];
	onChange: (field: OwnProps['name']) => void;
}

type Props = OwnProps;

const BaseCostInput: React.FC<Props> = (props) => {
	const {
		containerClassName,
		defaultValue,
		fieldsLength,
		readonly,
		rhfId,
		name,
		displayOnly,
		validFrom,
		control,
		readonlyFieldClassName,
		getValues,
		unregister,
		onChange,
	} = props;

	const editingCellName = useEditingTableCellName();

	const handleClick = React.useCallback(() => {
		if (readonly) {
			return;
		}
		editingCellName.setEditingTableCellName(name);
	}, [editingCellName, name, readonly]);

	const prefixGlyphs = React.useMemo(() => {
		if (!validFrom) {
			return undefined;
		}

		return [createValidFromGlyph(validFrom)];
	}, [validFrom]);

	return (
		<div className={containerClassName} onClick={handleClick}>
			{displayOnly ? (
				<DisplayCell
					elementKey={rhfId}
					name={name}
					prefixGlyphs={prefixGlyphs}
					readonly={true}
					type="dollar"
					value={defaultValue}
				/>
			) : (
				<InputCellRenderer
					control={control}
					defaultValue={defaultValue.toString()}
					fieldsLength={fieldsLength}
					getValues={getValues}
					name={name}
					onChange={onChange}
					prefixGlyphs={prefixGlyphs}
					readonly={readonly}
					readonlyFieldClassName={readonlyFieldClassName}
					rhfId={rhfId}
					type="dollar"
					unregister={unregister}
				/>
			)}
		</div>
	);
};

export default React.memo(BaseCostInput);
